import { useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { timeAgo } from "./shared";
import { Bell, CreditCard, FolderKanban, Users, ShoppingBag, Bot, Server, Check, CheckCheck, X } from "lucide-react";

const CAT = {
  payments: { label: "Payments", icon: CreditCard, cls: "text-emerald-400" },
  projects: { label: "Projects", icon: FolderKanban, cls: "text-gold" },
  clients: { label: "Clients", icon: Users, cls: "text-amber-300" },
  marketplace: { label: "Marketplace", icon: ShoppingBag, cls: "text-emerald-400" },
  ai: { label: "AI", icon: Bot, cls: "text-sky-400" },
  system: { label: "System", icon: Server, cls: "text-gray-400" },
};
const FILTERS = ["All", "Payments", "Projects", "Clients", "Marketplace", "AI", "System"];

export function NotificationBell({ onNav }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0 });
  const [filter, setFilter] = useState("All");
  const ref = useRef(null);

  const load = useCallback(() => api.get("/admin/notifications").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const markRead = async () => {
    await api.post("/admin/notifications/seen").catch(() => {});
    await load();
  };
  const clearAll = async () => {
    await api.post("/admin/notifications/clear").catch(() => {});
    await load();
  };

  const items = filter === "All" ? data.items : data.items.filter((i) => (CAT[i.category]?.label || "") === filter);

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((o) => !o)} data-testid="notif-bell" aria-label="Notifications"
        className="relative w-10 h-10 flex items-center justify-center border border-white/10 hover:border-gold/50 text-gray-300 hover:text-white transition-colors">
        <Bell size={18} />
        {data.unread > 0 && (
          <span data-testid="notif-badge" className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
            {data.unread > 9 ? "9+" : data.unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notif-dropdown" className="absolute right-0 mt-2 w-[360px] max-w-[92vw] bg-navy-900 border border-white/10 shadow-2xl z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <span className="font-heading text-white text-sm">Notifications {data.unread > 0 && <span className="text-gold">({data.unread})</span>}</span>
            <button onClick={() => setOpen(false)} className="text-gray-500 hover:text-white"><X size={16} /></button>
          </div>
          <div className="flex gap-1.5 px-3 py-2 border-b border-white/5 overflow-x-auto">
            {FILTERS.map((f) => (
              <button key={f} onClick={() => setFilter(f)} data-testid={`notif-filter-${f.toLowerCase()}`}
                className={`px-2.5 py-1 text-xs whitespace-nowrap transition-colors ${filter === f ? "bg-gold text-navy font-semibold" : "text-gray-400 hover:text-white"}`}>{f}</button>
            ))}
          </div>
          <div className="max-h-[340px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-10 text-center text-gray-500 text-sm">You're all caught up. 🎉</div>
            ) : items.map((n, i) => {
              const c = CAT[n.category] || CAT.system;
              return (
                <button key={i} onClick={() => { onNav(n.go); setOpen(false); }} data-testid={`notif-item-${i}`}
                  className={`w-full flex items-start gap-3 px-4 py-3 text-left border-b border-white/5 hover:bg-white/5 transition-colors ${n.unread ? "bg-white/[0.03]" : ""}`}>
                  <c.icon size={16} className={`mt-0.5 shrink-0 ${c.cls}`} />
                  <div className="min-w-0 flex-1">
                    <div className="text-gray-200 text-sm leading-snug">{n.text}</div>
                    <div className="text-gray-600 text-xs mt-0.5">{c.label} · {timeAgo(n.created_at)}</div>
                  </div>
                  {n.unread && <span className="mt-1.5 w-2 h-2 rounded-full bg-gold shrink-0" />}
                </button>
              );
            })}
          </div>
          <div className="flex items-center justify-between px-3 py-2.5 border-t border-white/10">
            <button onClick={markRead} data-testid="notif-mark-read" className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gold transition-colors"><CheckCheck size={13} /> Mark all read</button>
            <button onClick={clearAll} data-testid="notif-clear-all" className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gold transition-colors"><Check size={13} /> Clear all</button>
          </div>
        </div>
      )}
    </div>
  );
}
